import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../../../../../util/api";
import {
    Box,
    Chip,
    CircularProgress,
    Collapse,
    Container,
    Divider,
    InputAdornment,
    Paper,
    Skeleton,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import LockOutlinedIcon  from "@mui/icons-material/LockOutlined";
import KeyVisualModal from "../modal/KeyVisualModal.js";
import ActionButton from "../../../../../component/common/ActionButton";
import LoadingDots from "../../../../../component/common/LoadingDots";
import { ds } from "../../../../../styles/designSystem";
import { pickThumbnailSrc } from "../../../../../util/imageUrl";
import AppAutocomplete from "../../../../../component/common/AppAutocomplete";
import { notify } from "../../../../../util/toast";

const BASE     = process.env.REACT_APP_API_REAL_URL;
const API_REAL = process.env.REACT_APP_API_REAL_URL;

const activeChipSx = ds.components.external.chipActive;
const normalChipSx = ds.components.external.chip;

/* ════════════════════════════════════════════
   코드 입력 화면
════════════════════════════════════════════ */
function AccessGate({ onSuccess }) {
    const [code, setCode] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!code.trim()) return;
        setLoading(true);
        setError("");
        try {
            const data = await api.get(`${BASE}/public-api/key-visual/2d/external/verify`, { code: code.trim() });
            onSuccess(code.trim(), data);
        } catch (err) {
            setError(err.response?.data?.message || "유효하지 않은 접속 코드입니다.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: ds.colors.bg }}>
            <Box sx={{
                width: 360,
                ...ds.components.external.cardElevated,
                p: 4,
            }}>
                <Stack alignItems="center" spacing={2} mb={3}>
                    <Box sx={{ width: 52, height: 52, borderRadius: 1.5, bgcolor: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <LockOutlinedIcon sx={{ color: "#fff", fontSize: 26 }} />
                    </Box>
                    <Typography sx={ds.typography.pageTitle}>키비주얼 열람</Typography>
                    <Typography sx={{ ...ds.typography.pageSubtitle, textAlign: "center" }}>
                        담당자로부터 받은 접속 코드를 입력하세요
                    </Typography>
                </Stack>

                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        fullWidth size="small"
                        placeholder="접속 코드 입력"
                        value={code}
                        onChange={e => setCode(e.target.value)}
                        error={!!error}
                        helperText={error}
                        sx={{ mb: 2}}
                    />
                    <ActionButton
                        type="submit"
                        variant="primary"
                        fullWidth
                        loading={loading}
                        disabled={!code.trim()}
                        label={loading ? "확인 중..." : "입장하기"}
                        startIcon={false}
                        sx={{ py: 1.2 }}
                    />
                </Box>
            </Box>
        </Box>
    );
}

/* ════════════════════════════════════════════
   키비주얼 갤러리 — 직원 전용 목록(KeyVisualListPage)과 동일한 화면.
   외부 공개용이라 데이터는 접속 코드 기반 공개 API로 한 번에 받아
   필터링(키워드/키비주얼명/프로젝트명)은 클라이언트에서 처리하고,
   모달은 읽기 전용(수정/삭제 없음)으로 유지한다.
════════════════════════════════════════════ */
function KeyVisualGallery({ code, viewerInfo }) {
    const [posters, setPosters]           = useState([]);
    const [allKeywords, setAllKeywords]   = useState([]);
    const [activeKeywords, setActiveKeywords] = useState([]);
    const [activeName, setActiveName]     = useState("");
    const [activeProjectName, setActiveProjectName] = useState("");
    const [loadingPosters, setLoadingPosters] = useState(true);

    const [selectedPosterId, setSelectedPosterId]             = useState(null);
    const [selectedPosterSnapshot, setSelectedPosterSnapshot] = useState(null);
    const selectedPosterSnapshotRef = useRef(null);

    const [keywordOpen, setKeywordOpen]         = useState(true);
    const [keywordQuery, setKeywordQuery]       = useState("");
    const [showAllKeywords, setShowAllKeywords] = useState(false);
    const [autoCompleteValue, setAutoCompleteValue] = useState(null);

    /* 키비주얼 로드 (접속 기록도 함께 남김) */
    const loadPosters = useCallback((silent = false) => {
        setLoadingPosters(true);
        return api.get(`${BASE}/public-api/key-visual/2d/external/keyvisual`, { code })
            .then(rawData => {
                const list = Array.isArray(rawData) ? rawData : (rawData?.data || []);
                setPosters(list);
                // 키워드 추출
                const kwSet = new Set();
                list.forEach(p => (p.keywords || []).forEach(k => kwSet.add(k)));
                setAllKeywords([...kwSet].sort((a, b) => String(a).localeCompare(String(b), "ko")));
                if (!silent) notify("새로고침 완료", "success");
            })
            .catch(() => { if (!silent) notify("키비주얼 목록을 불러오지 못했습니다.", "error"); })
            .finally(() => setLoadingPosters(false));
    }, [code]);

    useEffect(() => { loadPosters(true); }, [loadPosters]);

    /* 프로젝트명 자동완성 옵션 — 로드된 키비주얼에서 추출 */
    const projectOptions = useMemo(() => {
        const set = new Set();
        posters.forEach(p => { if (p.projectName) set.add(p.projectName); });
        return [...set].sort((a, b) => String(a).localeCompare(String(b), "ko"));
    }, [posters]);

    /* 필터된 키비주얼 — 키워드(OR) + 키비주얼명(부분) + 프로젝트명(부분) */
    const filteredPosters = useMemo(() => {
        const nameQ = activeName.trim().toLowerCase();
        const projQ = activeProjectName.trim().toLowerCase();
        return posters.filter(p => {
            if (activeKeywords.length > 0 && !(p.keywords?.some(k => activeKeywords.includes(k)))) return false;
            if (nameQ && !String(p.name || "").toLowerCase().includes(nameQ)) return false;
            if (projQ && !String(p.projectName || "").toLowerCase().includes(projQ)) return false;
            return true;
        });
    }, [posters, activeKeywords, activeName, activeProjectName]);

    /* 선택된 키비주얼 */
    const selectedPoster = useMemo(() => {
        if (!selectedPosterId) return null;
        const found = posters.find(p => p.id === selectedPosterId);
        return found || selectedPosterSnapshotRef.current || selectedPosterSnapshot || null;
    }, [posters, selectedPosterId, selectedPosterSnapshot]);

    const toggleKeyword = useCallback(kw => {
        setActiveKeywords(prev => prev.includes(kw) ? prev.filter(k => k !== kw) : [...prev, kw]);
    }, []);

    const clearKeywords = () => setActiveKeywords([]);

    /* 키워드 렌더링 (숫자 연하게) */
    const renderKeywordLabel = useCallback(k => {
        const parts = String(k).split(/(\d+)/g);
        return (
            <span>
                {parts.map((p, i) => p ? (
                    <span key={i} style={
                        /^\d+$/.test(p)
                            ? { fontWeight: 700, color: "rgba(17,24,39,0.55)", letterSpacing: "-0.2px" }
                            : { fontWeight: 800, color: "rgba(17,24,39,0.85)", letterSpacing: "-0.2px" }
                    }>{p}</span>
                ) : null)}
            </span>
        );
    }, []);

    const filteredKeywords = useMemo(() => {
        const q = keywordQuery.trim().toLowerCase();
        return q ? allKeywords.filter(k => String(k).toLowerCase().includes(q)) : allKeywords;
    }, [allKeywords, keywordQuery]);

    const prioritizedKeywords = useMemo(() => {
        const activeSet = new Set(activeKeywords);
        return [...activeKeywords, ...filteredKeywords.filter(k => !activeSet.has(k))];
    }, [filteredKeywords, activeKeywords]);

    const KEYWORD_PREVIEW_COUNT = 40;
    const visibleKeywords = useMemo(() =>
        showAllKeywords ? prioritizedKeywords : prioritizedKeywords.slice(0, KEYWORD_PREVIEW_COUNT),
    [prioritizedKeywords, showAllKeywords]);

    const autoCompleteOptions = useMemo(() => {
        const q = keywordQuery.trim().toLowerCase();
        const base = q ? allKeywords.filter(k => String(k).toLowerCase().includes(q)) : allKeywords;
        const activeSet = new Set(activeKeywords);
        return base.filter(k => !activeSet.has(k)).slice(0, 5);
    }, [allKeywords, keywordQuery, activeKeywords]);

    const handleSelectFromAutocomplete = useCallback(selected => {
        if (!selected) return;
        const kw = String(selected);
        setActiveKeywords(prev => prev.includes(kw) ? prev : [kw, ...prev]);
        setKeywordOpen(true);
        setShowAllKeywords(true);
        setAutoCompleteValue(null);
        setKeywordQuery("");
    }, []);

    return (
        <>
            {/* 최소 헤더: 업체명만 */}
            <Box sx={{ bgcolor: ds.colors.surface, borderBottom: `1px solid ${ds.colors.border}`, px: 3, py: 1.2, display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box sx={{ width: 26, height: 26, borderRadius: 0.75, bgcolor: "#2563eb", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography sx={{ color: "#fff", fontWeight: 800, fontSize: 11 }}>ON</Typography>
                </Box>
                <Typography sx={{ fontWeight: 700, fontSize: 14, color: ds.colors.textPrimary }}>
                    {viewerInfo?.companyName}
                </Typography>
            </Box>

            {/* 직원 전용 목록과 동일한 메인 컨텐츠 */}
            <Container maxWidth="xl" sx={{ py: 3 }}>
                <Stack spacing={2.25}>
                    {/* 타이틀 + 새로고침 */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                            <Typography sx={ds.typography.pageTitle}>키비주얼 목록</Typography>
                            <Typography sx={{ ...ds.typography.pageSubtitle, mt: 0.5 }}>
                                키워드/프로젝트명으로 필터링하고 키비주얼을 미리볼 수 있습니다.
                            </Typography>
                        </Box>

                        <ActionButton
                            variant="refresh"
                            loading={loadingPosters}
                            onClick={() => loadPosters(false)}
                        />
                    </Stack>

                    {/* 검색 필터 */}
                    <Paper sx={{ ...ds.components.external.card, p: 2 }}>
                        <Stack spacing={1.25}>
                            {/* 키비주얼명 */}
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>
                                        키비주얼명
                                    </Typography>
                                </Stack>
                                <TextField
                                    value={activeName}
                                    onChange={(e) => setActiveName(e.target.value)}
                                    placeholder="키비주얼명 검색 (부분 일치)"
                                    size="small"
                                    sx={{ width: { xs: "100%", sm: 360 }, ...ds.components.external.inputSoft }}
                                />
                            </Stack>

                            <Divider sx={{ my: 0.25 }} />

                            {/* 프로젝트명 */}
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>
                                        프로젝트명
                                    </Typography>
                                </Stack>
                                <AppAutocomplete
                                    freeSolo
                                    options={projectOptions}
                                    value={activeProjectName}
                                    inputValue={activeProjectName}
                                    onChange={(v) => setActiveProjectName(v == null ? "" : String(v))}
                                    onInputChange={(v) => setActiveProjectName(v)}
                                    placeholder="프로젝트명 검색 (부분 일치)"
                                    variant="outlined" disableUnderline={false}
                                    textFieldSx={ds.components.external.inputSoft}
                                    sx={{ width: { xs: "100%", sm: 360 } }}
                                />
                            </Stack>

                            <Divider sx={{ my: 0.25 }} />

                            {/* 키워드 */}
                            <Stack
                                direction={{ xs: "column", sm: "row" }}
                                spacing={1}
                                alignItems={{ xs: "stretch", sm: "center" }}
                                justifyContent="space-between"
                            >
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.secondary" }}>키워드</Typography>
                                    <Chip
                                        label={keywordOpen ? "접기" : "열기"}
                                        size="small" clickable
                                        onClick={() => setKeywordOpen(p => !p)}
                                        sx={normalChipSx}
                                    />
                                </Stack>

                                <AppAutocomplete
                                    options={autoCompleteOptions}
                                    value={autoCompleteValue}
                                    inputValue={keywordQuery}
                                    onInputChange={(v) => { setKeywordQuery(v); setShowAllKeywords(false); }}
                                    onChange={(v) => handleSelectFromAutocomplete(v)}
                                    clearOnBlur={false}
                                    placeholder="키워드 검색"
                                    variant="outlined" disableUnderline={false}
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <SearchRoundedIcon fontSize="small" />
                                        </InputAdornment>
                                    }
                                    textFieldSx={ds.components.external.inputSoft}
                                    sx={{ width: { xs: "100%", sm: 360 } }}
                                />
                            </Stack>

                            <Collapse in={keywordOpen} timeout={180}>
                                <Stack spacing={1.25}>
                                    {activeKeywords.length > 0 && (
                                        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                                            <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.secondary", mr: 0.5 }}>활성</Typography>
                                            {activeKeywords.map(k => (
                                                <Chip key={`active-${k}`} label={renderKeywordLabel(k)} size="small" clickable
                                                    onClick={() => toggleKeyword(k)} sx={activeChipSx} />
                                            ))}
                                            <ActionButton variant="secondary" startIcon={false} onClick={clearKeywords} label="전체 해제" sx={{ ml: 0.5, px: 1.5 }} />
                                        </Stack>
                                    )}

                                    <Divider sx={{ my: 0.25 }} />

                                    <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
                                        <Chip label="전체" size="small" clickable onClick={clearKeywords}
                                            sx={activeKeywords.length === 0 ? activeChipSx : normalChipSx} />

                                        {loadingPosters ? (
                                            <>
                                                <Skeleton variant="rounded" width={90} height={28} />
                                                <Skeleton variant="rounded" width={70} height={28} />
                                                <Skeleton variant="rounded" width={110} height={28} />
                                            </>
                                        ) : visibleKeywords.map((k, i) => (
                                            <Chip key={`${k}-${i}`} label={renderKeywordLabel(k)} size="small" clickable
                                                onClick={() => toggleKeyword(k)}
                                                sx={activeKeywords.includes(k) ? activeChipSx : normalChipSx} />
                                        ))}
                                    </Stack>

                                    {!loadingPosters && prioritizedKeywords.length > KEYWORD_PREVIEW_COUNT && (
                                        <Box sx={{ display: "flex", justifyContent: "center" }}>
                                            <ActionButton variant="secondary" startIcon={false}
                                                onClick={() => setShowAllKeywords(p => !p)}
                                                label={showAllKeywords ? "접기" : `+ 더보기 (${prioritizedKeywords.length - KEYWORD_PREVIEW_COUNT}개)`}
                                                sx={{ px: 2 }} />
                                        </Box>
                                    )}
                                </Stack>
                            </Collapse>

                            <Divider sx={{ my: 0.5 }} />

                            <Typography sx={{ fontSize: 12, fontWeight: 800, color: "text.secondary" }}>
                                표시: <b>{filteredPosters.length}</b>개 / 전체 <b>{posters.length}</b>개
                                {!loadingPosters && <> · 키워드: <b>{filteredKeywords.length}</b>개</>}
                            </Typography>
                        </Stack>
                    </Paper>

                    {/* 첫 로딩 — 그리드 영역 안에 inline LoadingDots (헤더/필터/칩은 즉시 보임). */}
                    {loadingPosters && posters.length === 0 && (
                        <Box sx={{ py: 6 }}>
                            <LoadingDots />
                        </Box>
                    )}

                    {/* 데이터 0건 상태 — 필터로 인한 empty */}
                    {!loadingPosters && filteredPosters.length === 0 && posters.length > 0 && (
                        <Box sx={{ textAlign: "center", py: 8, color: ds.colors.textMuted }}>
                            <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 0.5 }}>
                                결과가 없습니다
                            </Typography>
                            <Typography sx={{ fontSize: 13 }}>
                                필터를 변경하거나 초기화해 보세요
                            </Typography>
                        </Box>
                    )}

                    {/* 키비주얼 그리드 — 직원 전용 목록과 동일 */}
                    <Box sx={{
                        display: "grid", gap: 2,
                        gridTemplateColumns: {
                            xs: "repeat(2, minmax(0, 1fr))",
                            md: "repeat(3, minmax(0, 1fr))",
                            lg: "repeat(4, minmax(0, 1fr))",
                            xl: "repeat(5, minmax(0, 1fr))",
                        },
                    }}>
                        {filteredPosters.map((poster) => (
                            <Paper
                                key={poster.id}
                                onClick={() => {
                                    setSelectedPosterId(poster.id);
                                    setSelectedPosterSnapshot(poster);
                                    selectedPosterSnapshotRef.current = poster;
                                }}
                                sx={{
                                    ...ds.components.external.contentCard,
                                    p: 1,
                                }}
                            >
                                <Box sx={{ position: "relative", borderRadius: 3, overflow: "hidden", bgcolor: ds.colors.bg }}>
                                    <Box
                                        component="img"
                                        src={pickThumbnailSrc(poster, API_REAL)}
                                        alt={poster.name}
                                        sx={{ width: "100%", height: 340, display: "block", objectFit: "cover" }}
                                    />
                                    {/* 제목 오버레이 — 이름 + 등록일 */}
                                    <Box sx={{
                                        position: "absolute", top: 0, left: 0, right: 0, p: 1.25,
                                        background: "linear-gradient(to bottom, rgba(0,0,0,0.28), rgba(0,0,0,0))",
                                    }}>
                                        <Typography noWrap sx={{
                                            color: "#fff", fontWeight: 800, fontSize: 14.5,
                                            letterSpacing: "-0.2px",
                                            textShadow: "0 1px 8px rgba(0,0,0,0.28)",
                                        }} title={poster.name}>
                                            {poster.name}
                                        </Typography>
                                        <Typography sx={{
                                            mt: 0.2,
                                            color: "rgba(255,255,255,0.75)",
                                            fontWeight: 800,
                                            fontSize: 12,
                                            textShadow: "0 1px 8px rgba(0,0,0,0.28)",
                                        }}>
                                            등록 {poster.createdAt ? String(poster.createdAt).slice(0, 16).replace("T", " ") : "-"}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Paper>
                        ))}
                    </Box>
                </Stack>

                {/* 키비주얼 모달 — 읽기 전용 (수정/삭제 콜백 없음) */}
                {selectedPosterId && selectedPoster && (
                    <KeyVisualModal
                        keyVisual={selectedPoster}
                        onClose={() => {
                            setSelectedPosterId(null);
                            setSelectedPosterSnapshot(null);
                            selectedPosterSnapshotRef.current = null;
                        }}
                        onUpdate={null}
                        onDeleted={null}
                        openSnack={() => {}}
                    />
                )}
            </Container>
        </>
    );
}

/* ════════════════════════════════════════════
   메인: URL 파라미터 자동 검증 → 코드 입력 or 갤러리
════════════════════════════════════════════ */
export default function ExternalKeyVisualPage() {
    const [accessCode, setAccessCode]   = useState(null);
    const [viewerInfo, setViewerInfo]   = useState(null);
    const [autoChecking, setAutoChecking] = useState(true);

    useEffect(() => {
        const code = new URLSearchParams(window.location.search).get("code");
        if (!code) { setAutoChecking(false); return; }

        api.get(`${BASE}/public-api/key-visual/2d/external/verify`, { code })
            .then(data => { setAccessCode(code); setViewerInfo(data); })
            .catch(() => {})
            .finally(() => setAutoChecking(false));
    }, []);

    if (autoChecking) {
        return (
            <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <CircularProgress sx={{ color: "#2563eb" }} />
            </Box>
        );
    }

    if (!accessCode) {
        return <AccessGate onSuccess={(code, info) => { setAccessCode(code); setViewerInfo(info); }} />;
    }

    return <KeyVisualGallery code={accessCode} viewerInfo={viewerInfo} />;
}
